import * as React from "react";

import { useTranslation } from "react-i18next";

import { XIcon } from "lucide-react";

import { buttonVariants } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export type PillProps = {
  label: string;
  isDefault: boolean;
  /** When provided, render a trailing ✕ that clears the whole selection. */
  onClear?: () => void;
  children: React.ReactNode;
};

export function Pill({ label, isDefault, onClear, children }: PillProps) {
  const { t } = useTranslation();
  return (
    <Popover>
      <div
        className={cn(
          "inline-flex items-center rounded-full border",
          isDefault
            ? "border-input text-muted-foreground"
            : "border-primary text-primary",
        )}
      >
        <PopoverTrigger
          className={cn(
            buttonVariants({ variant: "ghost", size: "sm" }),
            "rounded-full",
            onClear && "pr-1",
          )}
        >
          {label}
        </PopoverTrigger>
        {onClear && (
          <button
            type="button"
            aria-label={t("components.entitySelect.clear")}
            onClick={() => onClear()}
            className="text-primary/70 hover:bg-primary/10 hover:text-primary mr-1.5 rounded-full p-0.5"
          >
            <XIcon className="size-3.5" aria-hidden />
          </button>
        )}
      </div>
      <PopoverContent align="start" className="w-80 p-2">
        {children}
      </PopoverContent>
    </Popover>
  );
}
